import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Chip,
  Alert,
  Divider,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Settings,
  Build,
  BarChart,
  Warning,
  CheckCircle,
  Schedule,
  People,
  Delete,
  RotateLeft
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosInstance';
import apiConfig from '../config/appConfig';

const WorkerManagement = () => {
  const [workers, setWorkers] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [maxWorkers, setMaxWorkers] = useState(10);
  const [newWorkerId, setNewWorkerId] = useState('');
  const [reprocessLimit, setReprocessLimit] = useState(100);
  const [cleanupHours, setCleanupHours] = useState(24);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(apiConfig.endpoints.worker.base);
      setWorkers(response.data.data.workers);
      setStats(response.data.data.stats);
    } catch (error) {
      console.error('Error fetching worker data:', error);
      showSnackbar('Failed to fetch worker data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaxWorkers = async () => {
    try {
      const response = await axiosInstance.get(apiConfig.endpoints.worker.max);
      setMaxWorkers(response.data.data.maxWorkers);
    } catch (error) {
      console.error('Error fetching max workers:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchMaxWorkers();
    
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateWorker = async () => {
    try {
      setActionLoading(true);
      await axiosInstance.post(apiConfig.endpoints.worker.base, { workerId: newWorkerId || undefined });
      showSnackbar('Worker created successfully');
      setNewWorkerId('');
      fetchData();
    } catch (error) {
      console.error('Error creating worker:', error);
      showSnackbar(error.response?.data?.message || 'Failed to create worker', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopWorker = async (workerId) => {
    try {
      setActionLoading(true);
      await axiosInstance.delete(apiConfig.endpoints.worker.byId(workerId));
      showSnackbar(`Worker ${workerId} stopped successfully`);
      fetchData();
    } catch (error) {
      console.error('Error stopping worker:', error);
      showSnackbar(error.response?.data?.message || 'Failed to stop worker', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopAllWorkers = async () => {
    try {
      setActionLoading(true);
      const response = await axiosInstance.delete(apiConfig.endpoints.worker.base);
      showSnackbar(`Stopped ${response.data.data.stoppedCount} workers`);
      fetchData();
    } catch (error) {
      console.error('Error stopping all workers:', error);
      showSnackbar(error.response?.data?.message || 'Failed to stop all workers', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetMaxWorkers = async () => {
    try {
      setActionLoading(true);
      await axiosInstance.put(apiConfig.endpoints.worker.max, { maxWorkers });
      showSnackbar(`Maximum workers set to ${maxWorkers}`);
      fetchData();
    } catch (error) {
      console.error('Error setting max workers:', error);
      showSnackbar(error.response?.data?.message || 'Failed to set max workers', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReprocessFailedJobs = async () => {
    try {
      setActionLoading(true);
      const response = await axiosInstance.post(apiConfig.endpoints.worker.reprocessFailed, { limit: reprocessLimit });
      showSnackbar(`Reprocessed ${response.data.data.reprocessed} failed jobs`);
      fetchData();
    } catch (error) {
      console.error('Error reprocessing failed jobs:', error);
      showSnackbar(error.response?.data?.message || 'Failed to reprocess failed jobs', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCleanupOldJobs = async () => {
    try {
      setActionLoading(true);
      const response = await axiosInstance.post(apiConfig.endpoints.worker.cleanup, { ageInHours: cleanupHours });
      showSnackbar(`Cleaned up ${response.data.data.cleanedCount} old jobs`);
      fetchData();
    } catch (error) {
      console.error('Error cleaning up old jobs:', error);
      showSnackbar(error.response?.data?.message || 'Failed to clean up old jobs', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmDialog = (action, title, message) => {
    setConfirmDialog({ open: true, action, title, message });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null, title: '', message: '' });
  };

  const executeConfirmedAction = async () => {
    closeConfirmDialog();
    if (confirmDialog.action === 'stopAll') {
      await handleStopAllWorkers();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'success';
      case 'stopped':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Snackbar for Notifications */}
      {snackbar.open && (
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            position: 'fixed', 
            top: 20, 
            right: 20, 
            zIndex: 9999,
            minWidth: 300
          }}
        >
          {snackbar.message}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Worker Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchData}
          disabled={actionLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Queue Statistics */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <BarChart sx={{ mr: 1 }} />
              Queue Statistics
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {stats.waiting || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Waiting
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.active || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.completed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {stats.failed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="h4" color="secondary.main" fontWeight="bold">
                  {stats.delayed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Delayed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Box textAlign="center">
                <Typography variant="h4" color="text.primary" fontWeight="bold">
                  {stats.totalWorkers || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Workers
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Worker Controls */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Create Worker */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <PlayArrow sx={{ mr: 1 }} />
                  Create Worker
                </Box>
              }
            />
            <CardContent>
              <TextField
                fullWidth
                label="Worker ID (optional)"
                value={newWorkerId}
                onChange={(e) => setNewWorkerId(e.target.value)}
                placeholder="Enter custom worker ID"
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleCreateWorker}
                disabled={actionLoading || Object.keys(workers).length >= maxWorkers}
              >
                Create Worker
              </Button>
              {Object.keys(workers).length >= maxWorkers && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Maximum number of workers reached ({maxWorkers})
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Worker Settings */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <Settings sx={{ mr: 1 }} />
                  Worker Settings
                </Box>
              }
            />
            <CardContent>
              <Box display="flex" gap={2} sx={{ mb: 2 }}>
                <TextField
                  type="number"
                  label="Maximum Workers"
                  value={maxWorkers}
                  onChange={(e) => setMaxWorkers(parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 50 }}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleSetMaxWorkers}
                  disabled={actionLoading}
                >
                  Set
                </Button>
              </Box>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Stop />}
                onClick={() => openConfirmDialog('stopAll', 'Stop All Workers', 'Are you sure you want to stop all workers?')}
                disabled={actionLoading || Object.keys(workers).length === 0}
              >
                Stop All Workers
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Workers */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <People sx={{ mr: 1 }} />
              Active Workers ({Object.keys(workers).length})
            </Box>
          }
        />
        <CardContent>
          {Object.keys(workers).length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No active workers
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {Object.entries(workers).map(([workerId, worker]) => (
                <Grid item xs={12} sm={6} md={4} key={workerId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight="bold">
                          {workerId}
                        </Typography>
                        <Chip
                          label={worker.status}
                          color={getStatusColor(worker.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Created: {new Date(worker.createdAt).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Jobs Processed: {worker.jobsProcessed}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Jobs Failed: {worker.jobsFailed}
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Stop />}
                        onClick={() => handleStopWorker(workerId)}
                        disabled={actionLoading}
                      >
                        Stop Worker
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Queue Management */}
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <Build sx={{ mr: 1 }} />
              Queue Management
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            {/* Reprocess Failed Jobs */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Reprocess Failed Jobs
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Limit"
                value={reprocessLimit}
                onChange={(e) => setReprocessLimit(parseInt(e.target.value))}
                inputProps={{ min: 1, max: 1000 }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                color="warning"
                startIcon={<RotateLeft />}
                onClick={handleReprocessFailedJobs}
                disabled={actionLoading || (stats.failed || 0) === 0}
              >
                Reprocess Failed Jobs ({stats.failed || 0})
              </Button>
            </Grid>

            {/* Cleanup Old Jobs */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Cleanup Old Jobs
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Age (hours)"
                value={cleanupHours}
                onChange={(e) => setCleanupHours(parseInt(e.target.value))}
                inputProps={{ min: 1, max: 168 }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Delete />}
                onClick={handleCleanupOldJobs}
                disabled={actionLoading}
              >
                Cleanup Old Jobs
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Cancel</Button>
          <Button onClick={executeConfirmedAction} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkerManagement; 